import React, { useCallback, useEffect, useState } from "react";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { enableAutoRenew } from "scmp-sdk/components/rosetta/api";
import { useGA4CommonParameters, useSendImpressionEvents } from "scmp-sdk/components/rosetta/hooks";
import { SubscriptionButton } from "scmp-sdk/components/rosetta/subscription-button/styles";
import type { GA4Event } from "scmp-sdk/lib/google-analytics/ga4-types";

type Props = {
  className?: string;
};
export const AutoRenewButton: React.FunctionComponent<Props> = ({ className }) => {
  const { handleTrackGA4, rosettaInstance } = useSdkContext();
  const { computeGA4CommonParameters } = useGA4CommonParameters();
  const [showButton, setShowButton] = useState(false);
  useEffect(() => {
    if (rosettaInstance?.rulesEngine.getOutcome("user.autoRenewEnabled")) return;
    setShowButton(true);
  }, [rosettaInstance?.rulesEngine]);

  const { captureTrackImpressionEventTargetElement } = useSendImpressionEvents({
    ga4Event: useCallback<() => GA4Event>(
      () => ({
        action: "imp",
        category: "subs",
        customized_parameters: {
          ...computeGA4CommonParameters(),
          call_to_action: "enable auto-renew",
          trigger_point: "masthead renewal button",
        },
        subcategory: "entry_point",
      }),
      [computeGA4CommonParameters],
    ),
  });
  if (!showButton) return null;

  return (
    <SubscriptionButton
      className={className}
      onClick={() => {
        const execute = async () => {
          const autoRenewSubscriptionId = rosettaInstance?.rulesEngine.getOutcome(
            "user.autoRenewSubscriptionId",
          );
          handleTrackGA4({
            action: "click",
            category: "subs",
            customized_parameters: {
              ...computeGA4CommonParameters(),
              call_to_action: "enable auto-renew",
              trigger_point: "masthead enable auto-renew button",
            },
            subcategory: "entry_point",
          });
          if (!autoRenewSubscriptionId) return;

          const result = await enableAutoRenew(autoRenewSubscriptionId);
          if (!result) return;

          window.postMessage({ type: "PIANO_AUTORENEW_CONFIRMATION" }, "*");
          rosettaInstance?.rulesEngine.addRule(
            new rosettaInstance.Rule("user.autoRenewEnabled")
              .priority(10)
              .when(() => true)
              .then(true),
          );
          await rosettaInstance?.rulesEngine.run();
          setShowButton(false);
        };
        void execute();
      }}
      ref={captureTrackImpressionEventTargetElement}
    >
      Enable Auto‑renew
    </SubscriptionButton>
  );
};

AutoRenewButton.displayName = "AutoRenewButton";
